import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { checkMessage, type OutcomeMessage } from "@/modules/orders/tracking";
import { useSourceNames } from "@/modules/settings/logistics/useSourceNames";

/** The scope of a check of every source, as the server and the wait both name it. */
const EVERYTHING = "all";

export interface CheckNow {
  /** The scope being asked about now (`all` or a source), or null. */
  pendingScope: string | null;
  /** Whole seconds before `scope` would be accepted again; 0 when it would. */
  waitSeconds: (scope: string) => number;
  /** What the last ask came to, and which scope it was. */
  result: { scope: string; message: OutcomeMessage } | null;
  /** Ask. Ignored while one is in flight or while that scope is waiting. */
  check: (source?: string) => void;
}

/**
 * "Check now", for one source or for all (#527, Part B).
 *
 * The same discipline as refreshing an order: held by a ref the instant the
 * first click arrives so two clicks send one request, and held down for as long
 * as the server would refuse, counted out loud. Every outcome is an ordinary
 * answer with words — started, already running, nothing to check and why, just
 * checked, not connected — and none is thrown.
 *
 * A started check runs in the background and writes its row when it is done, so
 * the runs, the partners' counts and the unrecognised list are read again at
 * once, and once more a few seconds on, when a check of 150 shipments has
 * finished.
 */
export function useCheckNow(): CheckNow {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const nameOf = useSourceNames();

  const inFlight = useRef(false);
  const [waits, setWaits] = useState<Record<string, number>>({});
  const [clock, setClock] = useState(() => Date.now());
  const [result, setResult] = useState<CheckNow["result"]>(null);
  const [pendingScope, setPendingScope] = useState<string | null>(null);
  const later = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(
    () => () => {
      clearTimeout(later.current);
    },
    [],
  );

  const reread = () =>
    queryClient.invalidateQueries({ queryKey: ["tracking"] });

  const mutation = useMutation({
    mutationFn: async (source: string | undefined) => ({
      scope: source ?? EVERYTHING,
      outcome: await data.tracking.checkNow(source),
    }),
    onSuccess: ({ scope, outcome }) => {
      setResult({ scope, message: checkMessage(outcome, nameOf) });
      if (outcome.kind === "just_checked") {
        setWaits((current) => ({
          ...current,
          [scope]: Date.now() + outcome.retryAfterSeconds * 1000,
        }));
        setClock(Date.now());
      }
      if (outcome.kind === "started" || outcome.kind === "already_running") {
        // The server holds a scope to one manual check a minute.
        setWaits((current) => ({ ...current, [scope]: Date.now() + 60_000 }));
        setClock(Date.now());
        clearTimeout(later.current);
        later.current = setTimeout(() => {
          void reread();
        }, 6000);
      }
      void reread();
    },
    onError: (_error, source) => {
      setResult({
        scope: source ?? EVERYTHING,
        message: {
          tone: "problem",
          text: "Could not ask for a check. Check your connection and try again.",
        },
      });
    },
    onSettled: () => {
      inFlight.current = false;
      setPendingScope(null);
    },
  });

  const waiting = Object.values(waits).some((until) => until > clock);
  useEffect(() => {
    if (!waiting) return;
    const timer = setInterval(() => {
      setClock(Date.now());
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [waiting]);

  const waitSeconds = (scope: string) => {
    const until = waits[scope] ?? 0;
    return until > clock ? Math.ceil((until - clock) / 1000) : 0;
  };

  return {
    pendingScope,
    waitSeconds,
    result,
    check: (source) => {
      const scope = source ?? EVERYTHING;
      if (inFlight.current || waitSeconds(scope) > 0) return;
      inFlight.current = true;
      setPendingScope(scope);
      mutation.mutate(source);
    },
  };
}
